import React from "react";
import PropTypes from "prop-types";
import { useTranslate } from "./TranslationText";
import NewActionButton from "./ActionButtons/NewActionButton";

/**
 * Shared Pagination component across EAHS tables and services.
 * Follows EAHS pagination-buttons style and NewActionButton controls.
 *
 * @param {Object} props
 * @param {number} [props.currentPage=1] - Current active page number (1-based)
 * @param {number} [props.totalPages=1] - Total calculated pages
 * @param {boolean} [props.isLastPage=false] - Whether current page is the last page
 * @param {Function} props.onPageChange - Callback when a page is requested: (newPage) => void
 * @param {string} [props.className=""] - Additional classes for container
 */
export default function Pagination({
    currentPage = 1,
    totalPages = 1,
    isLastPage = false,
    onPageChange,
    className = "",
}) {
    const { translate } = useTranslate();

    if (totalPages <= 1 && currentPage === 1 && (isLastPage || totalPages <= 1)) {
        return null;
    }

    const isPrevDisabled = currentPage <= 1;
    const isNextDisabled = isLastPage || currentPage >= totalPages;

    return (
        <div className={`pagination-buttons rtl mb-4 d-flex justify-content-center align-items-center ${className}`.trim()}>
            <NewActionButton
                classNames="btn-outline-danger w-unset"
                key="prev"
                onClick={() => onPageChange && onPageChange(currentPage - 1)}
                disabled={isPrevDisabled}
                text={translate("web.previous", "Previous")}
            />
            <NewActionButton
                classNames="btn-danger w-unset ms-2"
                key="pages"
                text={`${currentPage} / ${totalPages}`}
            />
            <NewActionButton
                key="next"
                classNames="btn-outline-danger w-unset ms-2"
                onClick={() => onPageChange && onPageChange(currentPage + 1)}
                disabled={isNextDisabled}
                text={translate("web.next", "Next")}
            />
        </div>
    );
}

Pagination.propTypes = {
    currentPage: PropTypes.number,
    totalPages: PropTypes.number,
    isLastPage: PropTypes.bool,
    onPageChange: PropTypes.func.isRequired,
    className: PropTypes.string,
};

